import { Pencil, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { UploadPlaceholder } from "@/components/flowly/infra/UploadPlaceholder/UploadPlaceholder";
import { PhotoGuidelines } from "@/components/flowly/PhotoGuidelines/PhotoGuidelines";
import { ReferenceThumbnailStrip } from "@/components/flowly/ReferenceThumbnailStrip/ReferenceThumbnailStrip";
import { ACCEPTED_IMAGE_TYPES_STRING } from "@/config/media";

interface ImageGeneratorSourceUploadProps {
  imageUrl: string;
  maskImageUrl: string | null;
  isDragging: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onFileSelect: (file: File) => void;
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onClearImage: () => void;
  onOpenMask: () => void;
  referenceImages: Array<{ id: string; url: string }>;
  onReferenceImagesAdd: (files: FileList | File[]) => void;
  onReferenceImageRemove: (id: string) => void;
  maxReferenceImages: number;
}

export const ImageGeneratorSourceUpload = ({
  imageUrl,
  maskImageUrl,
  isDragging,
  fileInputRef,
  onFileSelect,
  onDrop,
  onDragOver,
  onDragLeave,
  onClearImage,
  onOpenMask,
  referenceImages,
  onReferenceImagesAdd,
  onReferenceImageRemove,
  maxReferenceImages,
}: ImageGeneratorSourceUploadProps) => {
  const { t } = useLanguage();

  return (
    <div className="flex flex-col gap-4" data-tour="source-upload">
      {imageUrl ? (
        <UploadPlaceholder
          src={imageUrl}
          label=""
          title={t.imageGenerator.originalImage}
          alt="Original"
        >
          {/* Mask overlay if exists */}
          {maskImageUrl && (
            <img
              src={maskImageUrl}
              alt="Mask overlay"
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />
          )}
          {/* Action buttons */}
          <div className="absolute top-2 left-2 flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); onClearImage(); }}
              className="p-1 rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onOpenMask(); }}
              className="px-2 py-1 rounded-lg bg-green-500/80 backdrop-blur-sm text-white text-xs font-bold hover:bg-green-500 transition-colors flex items-center gap-1.5"
            >
              <Pencil className="w-3.5 h-3.5" />
              {t.maskCanvas.title}
            </button>
          </div>
        </UploadPlaceholder>
      ) : (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES_STRING}
            onChange={(e) => e.target.files?.[0] && onFileSelect(e.target.files[0])}
            className="hidden"
          />
          <UploadPlaceholder
            src={null}
            label={t.imageGenerator.uploadImage}
            title={t.imageGenerator.uploadForEdit}
            isDragOver={isDragging}
            onClick={() => fileInputRef.current?.click()}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
          />

          {/* Photography Guidelines */}
          <PhotoGuidelines compact />
        </>
      )}

      {/* Reference Images */}
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-muted-foreground">
          {t.imageGenerator.referenceImages}
          {referenceImages.length > 0 && (
            <span className="ms-2 text-xs">
              {referenceImages.length}/{maxReferenceImages}
            </span>
          )}
        </span>
        <ReferenceThumbnailStrip
          images={referenceImages}
          onRemove={onReferenceImageRemove}
          onAdd={(files) => onReferenceImagesAdd(files)}
          maxImages={maxReferenceImages}
          hint={referenceImages.length === 0 ? t.imageGenerator.addReferenceHint : undefined}
        />
      </div>
    </div>
  );
};
